import { XMarkIcon } from '@heroicons/react/24/outline'
import { forwardRef, InputHTMLAttributes, MouseEvent, ReactNode } from 'react'

import { cn } from '@/app/utils'

export const checkboxBadgedTransitionDuration = 150

type Props = InputHTMLAttributes<HTMLInputElement> & {
  label?: ReactNode
  closable?: boolean
  className?: string
  labelClassName?: string
  extraContent?: ReactNode
  onIconClick?: (event?: MouseEvent) => void
  dataTestId?: string
}

const AppCheckboxBadged = forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      checked,
      extraContent,
      label,
      labelClassName,
      closable = true,
      onIconClick,
      dataTestId,
      ...props
    },
    ref
  ) => {
    const testIdPrefix = dataTestId?.replace(/-id$/, '')
    const onClick = (event: MouseEvent<SVGElement>) => {
      if (onIconClick) {
        event.preventDefault()
        event.stopPropagation()
        onIconClick(event)
      }
    }

    return (
      <label
        htmlFor={props.id}
        data-testid={dataTestId}
        className="relative inline-flex items-center gap-8"
      >
        <input
          ref={ref}
          type="checkbox"
          className="peer hidden"
          checked={checked}
          data-testid={testIdPrefix && `${testIdPrefix}-input-id`}
          {...props}
        />
        <span
          className={cn(
            'flex max-w-max items-center justify-center whitespace-nowrap rounded-full bg-grey-20 px-16 py-4 text-grey-100 peer-enabled:cursor-pointer peer-enabled:hover:bg-grey-30',
            'peer peer-checked:bg-grey-80 peer-checked:text-white peer-checked:peer-enabled:hover:bg-grey-80 peer-disabled:opacity-50',
            closable && 'peer-checked:pr-40',
            `transition-all duration-${checkboxBadgedTransitionDuration}`,
            className
          )}
        >
          {label && (
            <span className={cn('font-sans text-16', labelClassName)}>
              {label} {extraContent}
            </span>
          )}
        </span>

        {closable && (
          <XMarkIcon
            onClick={onClick}
            data-testid={
              testIdPrefix ? `${testIdPrefix}-clear-id` : 'x-mark-icon'
            }
            className="peer absolute right-16 -mr-8 ml-8 hidden w-24 border-l border-white/20 pl-8 text-white peer-checked:block peer-enabled:cursor-pointer"
          />
        )}
      </label>
    )
  }
)

AppCheckboxBadged.displayName = 'AppCheckboxBadged'
export default AppCheckboxBadged
